// ─── Notification bell + history panel (2026-07-30) ─────────────────────────
// Lives in the AdminLayout header. Admin + Confirmation Agent only: the hook
// returns enabled=false without `orders.view`, so this renders nothing for
// anyone else (and RLS would return no rows regardless).
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '@/lib/useNotifications';
import { useT } from '@/lib/i18n';
import type { NotificationKind } from '@/lib/api';
import { notificationHref } from '../lib/notificationHref';

const ACCENT: Record<NotificationKind, string> = {
  // status tokens (index.css): identical in light, readable in night mode
  'lead.new': 'var(--color-adm-gold)',
  'order.new': 'var(--color-status-done)',
  'delivery.problem': 'var(--color-status-stuck)',
  'message.failed': 'var(--color-status-stuck)',
  alert: 'var(--color-status-waiting)',
};

/** "il y a 5 min" — compact relative time, matching the Relances column style. */
function relTime(iso: string, t: (s: string, v?: Record<string, string | number>) => string): string {
  const ms = Date.now() - Date.parse(iso);
  if (!Number.isFinite(ms)) return '';
  const min = Math.floor(ms / 60000);
  if (min < 1) return t('À l’instant');
  if (min < 60) return t('il y a {n} min', { n: min });
  const h = Math.floor(min / 60);
  if (h < 24) return t('il y a {n} h', { n: h });
  const d = Math.floor(h / 24);
  return d === 1 ? t('hier') : t('il y a {n} j', { n: d });
}

export default function NotificationBell({ className = '' }: { className?: string }) {
  const { items, unread, markRead, markAllRead, enabled } = useNotifications();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { t } = useT();

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!enabled) return null;

  return (
    <div ref={wrapRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={unread > 0 ? t('Notifications ({n} non lues)', { n: unread }) : t('Notifications')}
        aria-expanded={open}
        className="relative w-10 h-10 rounded-xl border flex items-center justify-center
                   text-gray-500 hover:text-gray-800 bg-white transition-colors"
        style={{ borderColor: 'rgba(201,162,74,0.25)' }}
      >
        <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.4-1.4A2 2 0 0 1 18 14.2V11a6 6 0 1 0-12 0v3.2a2 2 0 0 1-.6 1.4L4 17h5m6 0a3 3 0 1 1-6 0m6 0H9" />
        </svg>
        {unread > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full grid place-items-center
                       text-[10px] font-bold tabular-nums text-white"
            style={{ background: 'var(--color-status-stuck-solid)' }}
          >
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          // MOBILE: pinned to the VIEWPORT, not the bell. `right-0` anchors to
          // the button, and the bell is not the last thing in the header (the
          // language switcher sits after it), so a 360px panel on a ~390px phone
          // ran off the left edge and clipped every title to "…ead #D401".
          // Fixed + left/right insets make the panel exactly as wide as the
          // screen allows, whatever else lives in the header.
          // DESKTOP (sm+): unchanged — anchored under the bell at a fixed width.
          className="fixed left-3 right-3 top-[68px]
                     sm:absolute sm:left-auto sm:right-0 sm:top-full sm:mt-2 sm:w-[360px]
                     max-h-[70vh] overflow-y-auto rounded-2xl border z-50"
          style={{
            background: 'var(--color-adm-surface)',
            backdropFilter: 'blur(10px)',
            borderColor: 'rgba(201,162,74,0.25)',
            boxShadow: 'var(--adm-pop-shadow)',
          }}
        >
          <div className="flex items-center justify-between gap-2 px-4 py-3 border-b sticky top-0"
            style={{ borderColor: 'rgba(201,162,74,0.16)', background: 'var(--color-adm-surface)' }}>
            <span className="text-[13px] font-semibold" style={{ fontFamily: "'Fraunces', Georgia, serif", color: 'var(--color-text-primary)' }}>
              {t('Notifications')}
            </span>
            {unread > 0 && (
              <button type="button" onClick={markAllRead}
                className="text-[11px] font-semibold hover:underline" style={{ color: 'var(--color-adm-gold-ink)' }}>
                {t('Tout marquer comme lu')}
              </button>
            )}
          </div>

          {items.length === 0 ? (
            <p className="px-4 py-8 text-center text-[12.5px] text-gray-400">
              {t('Aucune notification pour le moment.')}
            </p>
          ) : (
            <ul className="divide-y dark:divide-adm-hairline" style={{ borderColor: 'rgba(201,162,74,0.10)' }}>
              {items.map((n) => (
                <li key={n.id}>
                  <button
                    type="button"
                    onClick={() => {
                      markRead([n.id]);
                      setOpen(false);
                      const href = notificationHref(n);
                      if (href) navigate(href);
                    }}
                    className="w-full text-left flex items-start gap-2.5 px-4 py-3 hover:bg-gray-50/70 transition-colors"
                    style={{ background: n.read_at ? undefined : 'rgba(201,162,74,0.05)' }}
                  >
                    <span
                      aria-hidden="true"
                      className="shrink-0 mt-1 w-1.5 h-1.5 rounded-full"
                      style={{ background: n.read_at ? 'transparent' : (ACCENT[n.kind] ?? 'var(--color-adm-gold)') }}
                    />
                    <span className="min-w-0 flex-1">
                      <span className={`block text-[12.5px] leading-tight truncate ${n.read_at ? 'text-gray-600' : 'font-semibold text-gray-900'}`}>
                        {n.title}
                      </span>
                      {n.body && <span className="block text-[11px] text-gray-500 leading-snug mt-0.5 line-clamp-2">{n.body}</span>}
                      <span className="block text-[10.5px] text-gray-400 mt-1">{relTime(n.created_at, t)}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
